import { useQueryClient } from '@tanstack/react-query';

import { beCreateFee } from 'actions/taxes/beCreateFee.action';
import { beCreateTax } from 'actions/taxes/beCreateTax.action';
import Button from 'components/form/Button.component';
import { usePrepareWriteOnly } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';

import { AddRuleModal } from 'settings/taxes-and-fees/AddRuleModal';

export const AddRuleButton = ({ isDisabled }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { val: isRuleModalOpen, yay: showAddRuleModal, nay: hideAddRuleModal } = useTip(false);

  const { data: createTaxData, up: createTaxMutation, isLoading: isCreatingTax, isError: isCreatingTaxError } = usePrepareWriteOnly(beCreateTax);
  const { data: createFeeData, up: createFeeMutation, isLoading: isCreatingFee, isError: isCreatingFeeError } = usePrepareWriteOnly(beCreateFee);

  const hasTaxError = createTaxData?.json?.errors || createTaxData?.is?.ok === false || isCreatingTaxError;
  const hasFeeError = createFeeData?.json?.errors || createFeeData?.is?.ok === false || isCreatingFeeError;
  const hasError = hasTaxError || hasFeeError;

  const addRule = async (rule) => {
    if (isDisabled || isCreatingTax || isCreatingFee) return;

    if (rule.isTax) {
      const tax = {
        name: rule.name,
        percentage: rule.amount,
        appliedTo: rule.appliedTo,
        isDefault: rule.isDefault,
      };

      const response = await createTaxMutation({ data: tax });

      if (response?.is?.ok) {
        queryClient.invalidateQueries({ queryKey: ['beGetSalonTaxesAndFees'] });
        hideAddRuleModal();
      }
    } else {
      const fee = {
        name: rule.name,
        amount: rule.isAmount ? rule.amount : null,
        percentage: rule.isAmount ? null : rule.amount,
        appliedTo: rule.appliedTo,
        isDefault: rule.isDefault,
      };

      const response = await createFeeMutation({ data: fee });

      if (response?.is?.ok) {
        queryClient.invalidateQueries({ queryKey: ['beGetSalonTaxesAndFees'] });
        hideAddRuleModal();
      }
    }
  };

  return (
    <>
      <Button variant="primary" onClick={showAddRuleModal} tag="AddRuleButton" disabled={isDisabled}>
        {t('staffAndBusiness.taxesAndFees.addRuleButton')}
      </Button>
      {isRuleModalOpen && <AddRuleModal isOpen={isRuleModalOpen} onClose={hideAddRuleModal} onConfirm={addRule} isDisabled={isCreatingTax || isCreatingFee} hasError={hasError} />}
    </>
  );
};
